import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';

import { SignInFormSchema } from '../validations';

interface SignInProps {
  isLoading?: boolean;
  onSubmit: (
    data: z.infer<typeof SignInFormSchema>
  ) => Promise<{ error: string | null }>;
}

export function SignInForm({ onSubmit, isLoading }: SignInProps) {
  const form = useForm<z.infer<typeof SignInFormSchema>>({
    resolver: zodResolver(SignInFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const handleFormSubmit = async (data: z.infer<typeof SignInFormSchema>) => {
    const { error } = await onSubmit(data);
    if (error) {
      form.setError('password', { message: error });
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFormSubmit)}
        className="flex flex-col gap-4"
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  data-testid="sing-in-email"
                  placeholder="Email"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password</FormLabel>
              <FormControl>
                <PasswordInput data-testid="sing-in-password" {...field} />
              </FormControl>
              <FormMessage showQuestionMark />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          size="lg"
          isLoading={isLoading}
          data-testid="sing-in"
          disabled={!form.formState.isValid || isLoading}
          className="w-full self-end my-8"
        >
          Log in
        </Button>
      </form>
    </Form>
  );
}
export default SignInForm;
